import { Component, Injector, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, firstValueFrom } from 'rxjs';
import { PublicIpService } from './core/services/public-ip.service';
import { AppStateService } from './core/services/app-state.service';
import { StompService } from './core/services/stomp.service';
import { NotificationWsService } from './layouts/header/notification/notification-ws.service';
import { setRootInjector } from './core/config/injector-holder';
import { KeycloakPkceService } from './core/services/keycloak-pkce.service';
import { DpfService } from './core/services/dpf.service';
import { LocaleService } from './core/services/locale.service';
import { RouterPath } from './shared/models';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
    title = 'data-core';
    home = false;

    constructor(
        public router: Router,
        private publicIpService: PublicIpService,
        private injector: Injector,
        private stompService: StompService,
        /** Ensures STOMP destination watchers are registered before connect */
        _notificationWs: NotificationWsService,
        private appStateService: AppStateService,
        private keycloakPkce: KeycloakPkceService,
        private dpfService: DpfService,
        private localeService: LocaleService,
    ) {
        setRootInjector(injector);
        this.logBuildInfo();
    }

    private async handleKeycloakCallback(): Promise<void> {
        const callback = this.keycloakPkce.extractCallback();
        if (!callback) return;

        this.appStateService.nextLoginLoadingValue(true);
        try {
            const res: any = await firstValueFrom(this.dpfService.exchangeKeycloakCode(callback));
            if (res?.status && res?.data?.token) {
                localStorage.setItem('token', res.data.token);
                this.appStateService.nextTokenValue(true);
                await this.dpfService.authCheck(); // Call this after token is retrieved
                this.appStateService.nextTokenValue(true); // Call this after jwt is retrieved from authCheck()
                // A login started from a page (e.g. switching account on the HPC registration) goes back to it
                const returnUrl = this.keycloakPkce.takeReturnUrl();
                if (returnUrl) {
                    this.router.navigateByUrl(returnUrl);
                } else {
                    this.localeService.navigate(RouterPath.HOME);
                }
            }
        } finally {
            this.appStateService.nextLoginLoadingValue(false);
        }
    }

    private syncStompWithAuth(): void {
        const jwt = localStorage.getItem('jwt');
        if (jwt) {
            this.stompService.connectWithJwt(jwt);
        } else {
            this.stompService.disconnect();
        }
    }

    private logBuildInfo(): void {
        const commit = document.querySelector('meta[name="build-commit"]')?.getAttribute('content');
        const buildTime = document.querySelector('meta[name="build-time"]')?.getAttribute('content');

        if (commit) {
            console.log(`%c[Build] ${commit} - ${buildTime}`, 'color: #4CAF50;');
        } else {
            console.log('%c[Local Development]', 'color: #FF9800;');
        }
    }

    ngOnInit() {
        this.handleKeycloakCallback();
        this.syncStompWithAuth();
        this.appStateService.tokenObservable().subscribe((val) => {
            if (val) {
                this.syncStompWithAuth();
            }
        });
        this.publicIpService.loadPublicIp();
        this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe(() => {
                // Check if we're on the home page (considering locale prefix)
                const path = window.location.pathname;
                const isHomePage = path === '/' || /^\/(en|vi)\/?$/.test(path);
                this.home = isHomePage;
            });
    }
}
